'use client'

import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react'
import { Check, ChevronDown, Copy } from '@/components/ui/icons'

// ---------------------------------------------------------------------------
// DOM → Markdown conversion
//
// Doc page content is authored as JSX (single source of truth), so we derive a
// Markdown copy of it from the rendered DOM at click time rather than keeping a
// parallel Markdown file in sync. The converter only handles the element set
// used by the docs pages. Elements marked [data-copy-ignore] (e.g. nav chrome)
// are skipped; [data-copy-code] blocks become fenced code.
// ---------------------------------------------------------------------------

function inlineText(node: HTMLElement): string {
  let out = ''
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      out += child.textContent ?? ''
    } else if (child instanceof HTMLElement && child.getAttribute('aria-hidden') !== 'true') {
      out += inlineText(child)
    }
  }
  return out.replace(/\s+/g, ' ').trim()
}

function inlineMarkdown(node: HTMLElement): string {
  let out = ''
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      out += child.textContent ?? ''
      continue
    }
    if (!(child instanceof HTMLElement) || child.getAttribute('aria-hidden') === 'true') continue
    const tag = child.tagName.toLowerCase()
    if (tag === 'a') {
      const href = child.getAttribute('href') ?? ''
      const text = inlineMarkdown(child)
      out += href ? `[${text}](${href})` : text
    } else if (tag === 'code') {
      out += `\`${inlineText(child)}\``
    } else if (tag === 'strong' || tag === 'b') {
      out += `**${inlineMarkdown(child)}**`
    } else if (tag === 'em' || tag === 'i') {
      out += `*${inlineMarkdown(child)}*`
    } else {
      out += inlineMarkdown(child)
    }
  }
  return out.replace(/[ \t]+/g, ' ').trim()
}

function tableMarkdown(table: HTMLTableElement): string {
  const rows = Array.from(table.querySelectorAll('tr')).map(tr =>
    Array.from(tr.querySelectorAll('th, td')).map(cell => inlineMarkdown(cell as HTMLElement).replace(/\|/g, '\\|')),
  )
  if (rows.length === 0) return ''
  const [header, ...body] = rows
  const divider = header.map(() => '---')
  return [header, divider, ...body].map(cells => `| ${cells.join(' | ')} |`).join('\n')
}

function rootToMarkdown(root: HTMLElement): string {
  const lines: string[] = []
  const walk = (node: ChildNode) => {
    if (!(node instanceof HTMLElement)) return
    if (node.hasAttribute('data-copy-ignore') || node.getAttribute('aria-hidden') === 'true') return
    if (node.hasAttribute('data-copy-code')) {
      const code = (node.textContent ?? '').trim()
      // Tabbed samples keep every language in the DOM (hidden, not unmounted) so a
      // copied page carries all of them; the fence language tells them apart.
      const language = node.getAttribute('data-copy-lang') ?? ''
      if (code) lines.push('```' + language, code, '```', '')
      return
    }
    switch (node.tagName.toLowerCase()) {
      case 'h1':
        lines.push(`# ${inlineText(node)}`, '')
        return
      case 'h2':
        lines.push(`## ${inlineText(node)}`, '')
        return
      case 'h3':
        lines.push(`### ${inlineText(node)}`, '')
        return
      case 'p': {
        const text = inlineMarkdown(node)
        if (text) lines.push(text, '')
        return
      }
      case 'ul': {
        for (const li of Array.from(node.children)) {
          const text = inlineMarkdown(li as HTMLElement)
          if (text) lines.push(`- ${text}`)
        }
        lines.push('')
        return
      }
      case 'ol': {
        let i = 1
        for (const li of Array.from(node.children)) {
          const text = inlineMarkdown(li as HTMLElement)
          if (text) lines.push(`${i++}. ${text}`)
        }
        lines.push('')
        return
      }
      case 'table': {
        const table = tableMarkdown(node as HTMLTableElement)
        if (table) lines.push(table, '')
        return
      }
      default:
        for (const child of Array.from(node.childNodes)) walk(child)
    }
  }
  for (const child of Array.from(root.childNodes)) walk(child)
  return lines
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

async function writeClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

export function CopyableCode({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const [copied, setCopied] = useState(false)

  const onCopy = useCallback(async () => {
    const text = (ref.current?.textContent ?? '').trim()
    if (await writeClipboard(text)) {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    }
  }, [])

  return (
    <div className="group relative" data-copy-code>
      <div
        ref={ref}
        className="max-w-full overflow-x-auto whitespace-pre rounded-lg border border-border/60 bg-muted/20 py-3 pl-4 pr-12 font-mono text-xs text-foreground/90 md:text-sm"
      >
        {children}
      </div>
      <button
        type="button"
        onClick={onCopy}
        aria-label={copied ? 'Copied' : 'Copy'}
        className="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  )
}

export interface CodeSample {
  /** Tab label, e.g. "TypeScript", "Python", "curl". */
  label: string
  /** Fence language for the copied Markdown, e.g. "typescript", "python", "bash". */
  language: string
  code: string
}

const CODE_LANGUAGE_STORAGE_KEY = 'martini.docs.codeLanguage'
const CODE_LANGUAGE_EVENT = 'martini-docs-code-language'

/**
 * One code sample in several languages. Picking a tab picks that label for
 * every `CodeTabs` on the page (and the next visit), so a Python reader is not
 * re-choosing on each block. Inactive panels stay in the DOM so "Copy page"
 * and the assistant links carry every language.
 */
export function CodeTabs({ samples }: { samples: readonly CodeSample[] }) {
  // The shared preference is the tab LABEL ("TypeScript", "Python", "curl"), not the
  // fence language: an install sample's TypeScript tab is `bash`, its usage sample's
  // is `typescript`, and curl is `bash` too — keying on language would desync them.
  const [active, setActive] = useState(samples[0]?.label ?? '')

  useEffect(() => {
    const apply = (label: string | null) => {
      if (label && samples.some(sample => sample.label === label)) setActive(label)
    }
    try {
      apply(window.localStorage.getItem(CODE_LANGUAGE_STORAGE_KEY))
    } catch {
      // Storage can be unavailable (private mode); the first tab is a fine default.
    }
    const onChange = (event: Event) => apply((event as CustomEvent<string>).detail)
    window.addEventListener(CODE_LANGUAGE_EVENT, onChange)
    return () => window.removeEventListener(CODE_LANGUAGE_EVENT, onChange)
  }, [samples])

  const select = useCallback((label: string) => {
    setActive(label)
    try {
      window.localStorage.setItem(CODE_LANGUAGE_STORAGE_KEY, label)
    } catch {
      // Same as above: preference is a nicety, never a requirement.
    }
    window.dispatchEvent(new CustomEvent(CODE_LANGUAGE_EVENT, { detail: label }))
  }, [])

  return (
    <div className="rounded-lg border border-border/60 bg-muted/20">
      <div role="tablist" data-copy-ignore className="flex gap-1 border-b border-border/60 px-2 pt-1.5">
        {samples.map(sample => {
          const selected = sample.label === active
          return (
            <button
              key={sample.label}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => select(sample.label)}
              className={
                selected
                  ? '-mb-px border-b border-foreground px-2 pb-1.5 text-xs font-medium text-foreground'
                  : 'px-2 pb-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground'
              }
            >
              {sample.label}
            </button>
          )
        })}
      </div>
      {samples.map(sample => (
        <div key={sample.label} role="tabpanel" hidden={sample.label !== active}>
          <CodeTabPanel language={sample.language}>{sample.code}</CodeTabPanel>
        </div>
      ))}
    </div>
  )
}

function CodeTabPanel({ language, children }: { language: string; children: string }) {
  const [copied, setCopied] = useState(false)
  const onCopy = useCallback(async () => {
    if (await writeClipboard(children.trim())) {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    }
  }, [children])

  return (
    <div className="group relative" data-copy-code data-copy-lang={language}>
      <div className="max-w-full overflow-x-auto whitespace-pre py-3 pl-4 pr-12 font-mono text-xs text-foreground/90 md:text-sm">
        {children}
      </div>
      <button
        type="button"
        onClick={onCopy}
        aria-label={copied ? 'Copied' : 'Copy'}
        className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  )
}

export function CopyTextButton({ text, label, copiedLabel }: { text: string; label: string; copiedLabel: string }) {
  const [copied, setCopied] = useState(false)

  const onCopy = useCallback(async () => {
    if (await writeClipboard(text)) {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    }
  }, [text])

  return (
    <button
      type="button"
      onClick={onCopy}
      className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-card/40 px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-border hover:text-foreground"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? copiedLabel : label}
    </button>
  )
}

const menuItemClass =
  'flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground'

function subscribeCodeLanguage(onChange: () => void): () => void {
  window.addEventListener(CODE_LANGUAGE_EVENT, onChange)
  return () => window.removeEventListener(CODE_LANGUAGE_EVENT, onChange)
}

function readCodeLanguage(): string | null {
  try {
    return window.localStorage.getItem(CODE_LANGUAGE_STORAGE_KEY)
  } catch {
    return null
  }
}

/** The tab label the reader last picked on any `CodeTabs`, live; `null` on the server and before any pick. */
function useCodeLanguageLabel(): string | null {
  return useSyncExternalStore(subscribeCodeLanguage, readCodeLanguage, () => null)
}

export interface PromptOption {
  /** Shown on the button and in the menu, e.g. "TypeScript". */
  label: string
  /** One line under the label in the menu, e.g. "@martini-film/client". */
  detail: string
  /** The `CodeTabs` labels that make this the default; "Any language" claims "curl". */
  forCodeLabels: readonly string[]
  text: string
}

/**
 * "Copy LLM prompt" with a language choice. The main button copies the prompt
 * for the language the reader is already viewing in the page's code tabs
 * (TypeScript unless they picked Python, or curl → the language-agnostic HTTP
 * guide); the menu offers every prompt explicitly.
 */
export function CopyPromptMenu({ prompts }: { prompts: readonly PromptOption[] }) {
  const containerRef = useRef<HTMLDivElement>(null)
  // One timer for the "Copied …" flash: a second copy within 1.8 s replaces it, so an older
  // timeout cannot clear the newer label, and unmount cancels it. The sequence number lets a
  // clipboard write that finishes after a newer one started leave the newer feedback alone.
  const copiedTimer = useRef<number | null>(null)
  const copySequence = useRef(0)
  const [open, setOpen] = useState(false)
  const [copiedLabel, setCopiedLabel] = useState<string | null>(null)
  const codeLabel = useCodeLanguageLabel()

  const preferred = prompts.find(prompt => codeLabel && prompt.forCodeLabels.includes(codeLabel)) ?? prompts[0]

  const copy = useCallback(async (prompt: PromptOption) => {
    setOpen(false)
    const sequence = ++copySequence.current
    if ((await writeClipboard(prompt.text)) && sequence === copySequence.current) {
      setCopiedLabel(prompt.label)
      if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current)
      copiedTimer.current = window.setTimeout(() => {
        copiedTimer.current = null
        setCopiedLabel(null)
      }, 1800)
    }
  }, [])

  useEffect(
    () => () => {
      if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current)
    },
    [],
  )

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (!preferred) return null

  return (
    <div ref={containerRef} className="relative flex shrink-0 items-center">
      <button
        type="button"
        onClick={() => void copy(preferred)}
        data-testid="copy-prompt-default"
        className="inline-flex items-center gap-1.5 rounded-l-md border border-border/60 bg-card/40 px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-border hover:text-foreground"
      >
        {copiedLabel ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        {copiedLabel ? `Copied ${copiedLabel} prompt` : 'Copy LLM prompt'}
        {!copiedLabel && <span className="font-normal text-muted-foreground/70">· {preferred.label}</span>}
      </button>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label="Choose a prompt language"
        aria-haspopup="menu"
        aria-expanded={open}
        className="-ml-px inline-flex items-center justify-center rounded-r-md border border-border/60 bg-card/40 px-1.5 py-1.5 text-muted-foreground transition-colors hover:border-border hover:text-foreground"
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-20 mt-1 w-64 rounded-lg border border-border/60 bg-card p-1 shadow-lg"
        >
          {prompts.map(prompt => (
            <button
              key={prompt.label}
              type="button"
              role="menuitem"
              onClick={() => void copy(prompt)}
              className={`${menuItemClass} flex-col items-start gap-0`}
            >
              <span className="text-foreground">{prompt.label}</span>
              <span className="text-[11px] text-muted-foreground">{prompt.detail}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function DocsCopyToolbar({
  rootId,
  assistantPrompt,
}: {
  rootId: string
  /** Prompt sent to the "Open in ChatGPT/Claude" links; phrase it for what this specific doc page covers, and include the page URL. */
  assistantPrompt: string
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [copied, setCopied] = useState(false)
  const [open, setOpen] = useState(false)

  const copyPage = useCallback(async () => {
    const root = document.getElementById(rootId)
    const markdown = root ? rootToMarkdown(root) : ''
    if (await writeClipboard(markdown)) {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    }
  }, [rootId])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const chatgptHref = `https://chatgpt.com/?q=${encodeURIComponent(assistantPrompt)}`
  const claudeHref = `https://claude.ai/new?q=${encodeURIComponent(assistantPrompt)}`

  return (
    <div ref={containerRef} className="relative flex shrink-0 items-center">
      <button
        type="button"
        onClick={copyPage}
        className="inline-flex items-center gap-1.5 rounded-l-md border border-border/60 bg-card/40 px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-border hover:text-foreground"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        {copied ? 'Copied' : 'Copy page'}
      </button>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label="More copy options"
        aria-expanded={open}
        className="-ml-px inline-flex items-center justify-center rounded-r-md border border-border/60 bg-card/40 px-1.5 py-1.5 text-muted-foreground transition-colors hover:border-border hover:text-foreground"
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-20 mt-1 w-48 rounded-lg border border-border/60 bg-card p-1 shadow-lg">
          <button
            type="button"
            onClick={() => {
              void copyPage()
              setOpen(false)
            }}
            className={menuItemClass}
          >
            <Copy className="h-3.5 w-3.5" />
            Copy page as Markdown
          </button>
          <a
            href={chatgptHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className={menuItemClass}
          >
            Open in ChatGPT
          </a>
          <a
            href={claudeHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className={menuItemClass}
          >
            Open in Claude
          </a>
        </div>
      )}
    </div>
  )
}
